/**
 * @module popoverPositionHelper
 * @description A utility function to calculate and apply the correct position for a popover element.
 */

/**
 * The gap in pixels between the trigger element and the popover content.
 * @type {number}
 */
const POPOVER_GAP = 8;

/**
 * Calculates and sets the position of a popover content element relative to a trigger element.
 * It handles various placements and ensures the popover stays within the viewport boundaries.
 *
 * @param {object} options - The positioning options.
 * @param {UIDiv} options.trigger - The UI element that triggers the popover.
 * @param {UIDiv} options.content - The UI element representing the popover's content.
 * @param {string} options.placement - The desired placement of the popover (e.g., 'bottom-start', 'top', 'right-end').
 */
export function updatePosition({ trigger, content, placement }) {
  // Get the DOM elements from the UI wrappers
  const triggerEl = trigger.dom;
  const contentEl = content.dom;

  // If either element doesn't exist, we can't proceed.
  if (!triggerEl || !contentEl) return;

  // Get the bounding rectangles for the trigger, content, and the positioning container (offsetParent).
  // getBoundingClientRect provides position relative to the viewport.
  const triggerRect = triggerEl.getBoundingClientRect();
  const contentRect = contentEl.getBoundingClientRect();

  // Split placement into primary position (e.g., 'bottom') and alignment (e.g., 'start')
  const [position, align] = placement.split('-');

  let top, left;

  // --- Step 1: Calculate Ideal Position based on Placement ---

  // Calculate position based on the primary placement ('top', 'bottom', 'left', 'right')
  switch (position) {
    case 'top':
      top = triggerRect.top - contentRect.height - POPOVER_GAP;
      break;
    case 'left':
      left = triggerRect.left - contentRect.width - POPOVER_GAP;
      break;
    case 'right':
      left = triggerRect.right + POPOVER_GAP;
      break;
    default: // 'bottom'
      top = triggerRect.bottom + POPOVER_GAP;
      break;
  }

  // Calculate alignment based on the secondary alignment ('start', 'end', or centered by default)
  if (position === 'top' || position === 'bottom') {
    switch (align) {
      case 'start':
        left = triggerRect.left;
        break;
      case 'end':
        left = triggerRect.right - contentRect.width;
        break;
      default: // Centered
        left = triggerRect.left + triggerRect.width / 2 - contentRect.width / 2;
    }
  } else if (position === 'left' || position === 'right') {
    switch (align) {
      case 'start':
        top = triggerRect.top;
        break;
      case 'end':
        top = triggerRect.bottom - contentRect.height;
        break;
      default: // Centered
        top = triggerRect.top + triggerRect.height / 2 - contentRect.height / 2;
    }
  }

  // --- Step 2: Viewport Collision Detection & Correction ---

  // Check for horizontal overflow
  if (left + contentRect.width > window.innerWidth) {
    left = window.innerWidth - contentRect.width - POPOVER_GAP;
  }
  if (left < 0) {
    left = POPOVER_GAP;
  }

  // Check for vertical overflow
  if (top + contentRect.height > window.innerHeight) {
    // If it overflows at the bottom, try to flip it to the top
    const flippedTop = triggerRect.top - contentRect.height - POPOVER_GAP;
    if (flippedTop > 0) {
      top = flippedTop;
    } else {
      // If flipping also causes overflow, clamp it to the bottom of the viewport
      top = window.innerHeight - contentRect.height - POPOVER_GAP;
    }
  }
  if (top < 0) {
    // If it overflows at the top, try to flip it to the bottom
    const flippedBottom = triggerRect.bottom + POPOVER_GAP;
    if (flippedBottom + contentRect.height < window.innerHeight) {
      top = flippedBottom;
    } else {
      // If flipping also causes overflow, clamp it to the top of the viewport
      top = POPOVER_GAP;
    }
  }

  // --- Step 3: Apply Positioning ---
  content.setStyle('position', ['fixed']);
  content.setStyle('top', [`${top}px`]);
  content.setStyle('left', [`${left}px`]);
  content.setStyle('z-index', ['9999']); // High z-index to ensure it appears above everything
}
